/**
 * C1 delivery checkout — two PaymentIntents on merchant Connect:
 * 1) Food + BookAPT application fee ($0.25 service fee)
 * 2) Delivery + tip
 */

import React, { useEffect, useState } from 'react';
import {
  Modal,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  ScrollView,
  Platform,
} from 'react-native';
import { getStripe } from '../services/webStripeCheckout';
import type { DeliveryCheckoutIntents } from '../types/delivery';
import { formatCentsUsd } from '../types/delivery';

let StripeElementsLib: any = null;
async function loadStripeElementsLib() {
  if (!StripeElementsLib) {
    StripeElementsLib = await import('@stripe/react-stripe-js');
  }
  return StripeElementsLib;
}

export interface WebStripeDeliveryCheckoutResult {
  success: boolean;
  orderId?: string;
  foodPaymentIntentId?: string;
  logisticsPaymentIntentId?: string;
  error?: string;
  cancelled?: boolean;
}

interface WebStripeDeliveryCheckoutModalProps {
  visible: boolean;
  checkout: DeliveryCheckoutIntents | null;
  onClose: () => void;
  onResult: (result: WebStripeDeliveryCheckoutResult) => void;
}

const PaymentStep: React.FC<{
  clientSecret: string;
  stepLabel: string;
  amountCents: number;
  onConfirmed: (paymentIntentId: string, paymentMethodId: string) => void;
  onError: (message: string, cancelled?: boolean) => void;
  autoConfirmPaymentMethodId?: string;
}> = ({
  clientSecret,
  stepLabel,
  amountCents,
  onConfirmed,
  onError,
  autoConfirmPaymentMethodId,
}) => {
  const [lib, setLib] = useState<any>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadStripeElementsLib().then((mod) => {
      if (!cancelled) setLib(mod);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!autoConfirmPaymentMethodId || !lib) return;
    let cancelled = false;
    (async () => {
      setSubmitting(true);
      const stripe = await getStripe();
      if (!stripe || cancelled) return;
      const { error: confirmErr, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        { payment_method: autoConfirmPaymentMethodId }
      );
      setSubmitting(false);
      if (cancelled) return;
      if (confirmErr) {
        onError(confirmErr.message || 'Payment failed');
        return;
      }
      if (paymentIntent?.status === 'succeeded' || paymentIntent?.status === 'processing') {
        const pm =
          typeof paymentIntent.payment_method === 'string'
            ? paymentIntent.payment_method
            : paymentIntent.payment_method?.id || autoConfirmPaymentMethodId;
        onConfirmed(paymentIntent.id, pm);
      } else {
        onError(`Unexpected status: ${paymentIntent?.status}`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [autoConfirmPaymentMethodId, lib, clientSecret]);

  if (!lib) {
    return (
      <View style={styles.loadingState}>
        <ActivityIndicator size="small" color="#007AFF" />
      </View>
    );
  }

  const { Elements, PaymentElement, useStripe, useElements } = lib;

  const InnerForm: React.FC = () => {
    const stripe = useStripe();
    const elements = useElements();

    const handlePay = async () => {
      if (!stripe || !elements) return;
      setError(null);
      setSubmitting(true);
      const submitResult = await elements.submit();
      if (submitResult?.error) {
        setSubmitting(false);
        setError(submitResult.error.message || 'Validation failed');
        return;
      }
      const { error: confirmErr, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });
      setSubmitting(false);
      if (confirmErr) {
        setError(confirmErr.message || 'Payment failed');
        onError(confirmErr.message || 'Payment failed', confirmErr.type === 'validation_error');
        return;
      }
      const pm =
        paymentIntent && typeof paymentIntent.payment_method === 'string'
          ? paymentIntent.payment_method
          : (paymentIntent?.payment_method as { id?: string } | undefined)?.id || '';
      if (
        paymentIntent &&
        (paymentIntent.status === 'succeeded' || paymentIntent.status === 'processing')
      ) {
        onConfirmed(paymentIntent.id, pm);
      } else {
        const msg = `Unexpected payment status: ${paymentIntent?.status}`;
        setError(msg);
        onError(msg);
      }
    };

    if (autoConfirmPaymentMethodId) {
      return (
        <View style={styles.loadingState}>
          <ActivityIndicator size="small" color="#007AFF" />
          <Text style={styles.loadingText}>Confirming {stepLabel}…</Text>
        </View>
      );
    }

    return (
      <View>
        <Text style={styles.stepTitle}>
          {stepLabel} — {formatCentsUsd(amountCents)}
        </Text>
        <View style={styles.elementHost}>
          <PaymentElement options={{ layout: 'tabs', wallets: { applePay: 'auto', googlePay: 'auto' } }} />
        </View>
        {error ? <Text style={styles.errorText}>{error}</Text> : null}
        <TouchableOpacity
          style={[styles.payButton, submitting && styles.payButtonDisabled]}
          onPress={handlePay}
          disabled={submitting}
        >
          {submitting ? (
            <ActivityIndicator color="#fff" />
          ) : (
            <Text style={styles.payButtonText}>Pay {formatCentsUsd(amountCents)}</Text>
          )}
        </TouchableOpacity>
      </View>
    );
  };

  const stripePromise = getStripe();
  return (
    <Elements stripe={stripePromise} options={{ clientSecret }}>
      <InnerForm />
    </Elements>
  );
};

const WebStripeDeliveryCheckoutModal: React.FC<WebStripeDeliveryCheckoutModalProps> = ({
  visible,
  checkout,
  onClose,
  onResult,
}) => {
  const [phase, setPhase] = useState<'food' | 'logistics' | 'done'>('food');
  const [paymentMethodId, setPaymentMethodId] = useState<string | null>(null);
  const [foodPiId, setFoodPiId] = useState<string | null>(null);

  useEffect(() => {
    if (visible && checkout) {
      setPhase('food');
      setPaymentMethodId(null);
      setFoodPiId(null);
    }
  }, [visible, checkout?.foodClientSecret]);

  if (Platform.OS !== 'web' || !checkout?.success) {
    return null;
  }

  const logisticsCents = checkout.deliveryFeeCents + checkout.tipCents;

  const handleFoodConfirmed = (piId: string, pmId: string) => {
    setFoodPiId(piId);
    setPaymentMethodId(pmId);
    setPhase('logistics');
  };

  const handleLogisticsConfirmed = (piId: string) => {
    setPhase('done');
    onResult({
      success: true,
      orderId: checkout.orderId,
      foodPaymentIntentId: foodPiId || checkout.foodPaymentIntentId,
      logisticsPaymentIntentId: piId,
    });
  };

  const handleFail = (message: string, cancelled?: boolean) => {
    onResult({ success: false, error: message, cancelled });
  };

  return (
    <Modal visible={visible} animationType="slide" transparent onRequestClose={onClose}>
      <View style={styles.overlay}>
        <View style={styles.sheet}>
          <ScrollView>
            <Text style={styles.title}>Delivery checkout</Text>
            <View style={styles.summary}>
              <View style={styles.row}>
                <Text style={styles.label}>Subtotal</Text>
                <Text style={styles.value}>{formatCentsUsd(checkout.subtotalCents)}</Text>
              </View>
              <View style={styles.row}>
                <Text style={styles.label}>Delivery fee</Text>
                <Text style={styles.value}>{formatCentsUsd(checkout.deliveryFeeCents)}</Text>
              </View>
              <View style={styles.row}>
                <Text style={styles.label}>Service fee</Text>
                <Text style={styles.value}>{formatCentsUsd(checkout.serviceFeeCents)}</Text>
              </View>
              {checkout.tipCents > 0 ? (
                <View style={styles.row}>
                  <Text style={styles.label}>Tip</Text>
                  <Text style={styles.value}>{formatCentsUsd(checkout.tipCents)}</Text>
                </View>
              ) : null}
              <View style={[styles.row, styles.totalRow]}>
                <Text style={styles.totalLabel}>Total</Text>
                <Text style={styles.totalValue}>{formatCentsUsd(checkout.totalCents)}</Text>
              </View>
            </View>

            {phase === 'food' && checkout.foodClientSecret ? (
              <PaymentStep
                clientSecret={checkout.foodClientSecret}
                stepLabel="Order"
                amountCents={checkout.subtotalCents}
                onConfirmed={handleFoodConfirmed}
                onError={handleFail}
              />
            ) : null}

            {phase === 'logistics' && checkout.logisticsClientSecret ? (
              <PaymentStep
                clientSecret={checkout.logisticsClientSecret}
                stepLabel="Delivery & tip"
                amountCents={logisticsCents}
                autoConfirmPaymentMethodId={paymentMethodId || undefined}
                onConfirmed={handleLogisticsConfirmed}
                onError={handleFail}
              />
            ) : null}

            {phase === 'done' ? (
              <Text style={styles.successText}>Payment complete. Dispatching your order…</Text>
            ) : null}
          </ScrollView>
          <TouchableOpacity style={styles.closeButton} onPress={onClose}>
            <Text style={styles.closeButtonText}>Cancel</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end',
  },
  sheet: {
    backgroundColor: '#fff',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    maxHeight: '90%',
    padding: 20,
  },
  title: { fontSize: 20, fontWeight: '700', marginBottom: 12 },
  summary: { marginBottom: 16, padding: 12, backgroundColor: '#f8f9fa', borderRadius: 8 },
  row: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 6 },
  label: { color: '#555', fontSize: 14 },
  value: { fontSize: 14, fontWeight: '500' },
  totalRow: { marginTop: 8, paddingTop: 8, borderTopWidth: 1, borderTopColor: '#e1e5e9' },
  totalLabel: { fontSize: 16, fontWeight: '700' },
  totalValue: { fontSize: 16, fontWeight: '700' },
  stepTitle: { fontSize: 15, fontWeight: '600', marginBottom: 8 },
  elementHost: { marginBottom: 12, minHeight: 120 },
  payButton: {
    backgroundColor: '#007AFF',
    padding: 14,
    borderRadius: 10,
    alignItems: 'center',
  },
  payButtonDisabled: { opacity: 0.6 },
  payButtonText: { color: '#fff', fontWeight: '700', fontSize: 16 },
  errorText: { color: '#c00', marginBottom: 8 },
  loadingState: { padding: 16, alignItems: 'center' },
  loadingText: { marginTop: 8, color: '#666' },
  successText: { color: '#0a7', fontWeight: '600', textAlign: 'center', padding: 16 },
  closeButton: { marginTop: 12, padding: 12, alignItems: 'center' },
  closeButtonText: { color: '#666' },
});

export default WebStripeDeliveryCheckoutModal;
